/**
 * ChatPanel — Right-side sliding drawer with expandable full-screen mode.
 *
 * Architecture: Slack/Rippling-inspired sliding panel.
 * Uses useChat hook for state + WebSocket management.
 *
 * Состав файла: orchestrator (state-холдер для useChat + WS lifecycle,
 * permission checks, реакции/удаления, jump-to-message) + composition
 * презентационных частей из `./sections/`.
 *
 * Re-exports RoomList / MessageArea / MessageInput / ChatHeader /
 * RoomInfoPanel — оставлены как named exports, потому что MessengerPage
 * импортирует их именно отсюда.
 */
import { useState, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useChat } from '../../hooks/useChat'
import { MessageCircle, X, Maximize2, Minimize2, Info } from 'lucide-react'
import { chatApi } from '../../api/chat'
import RoomList from './sections/RoomList'
import MessageArea from './sections/MessageArea'
import MessageInput from './sections/MessageInput'
import ChatHeader from './sections/ChatHeader'
import RoomInfoPanel from './sections/RoomInfoPanel'
import NewChatDialog from './NewChatDialog'
import '../../styles/chat.css'

export { RoomList, MessageArea, MessageInput, ChatHeader, RoomInfoPanel }

export default function ChatPanel() {
    const location = useLocation()
    const { user } = useAuth()
    const chat = useChat(user?.id)
    const [isOpen, setIsOpen] = useState(false)
    const [isExpanded, setIsExpanded] = useState(false)
    const [showNewChat, setShowNewChat] = useState(false)
    const [newChatTab, setNewChatTab] = useState('direct')
    const canCreateChannel = ['admin', 'ceo', 'dept_head', 'cmo'].includes(user?.role || '')
    const [replyTo, setReplyTo] = useState(null)
    const [showRoomInfo, setShowRoomInfo] = useState(false)

    useEffect(() => {
        chat.connect()
        chat.loadRooms()
        return () => chat.disconnect()
    }, [chat.connect, chat.disconnect, chat.loadRooms])

    // External open trigger from AssistantsMenu (topbar dropdown).
    // FAB-кнопка `.chat-toggle` удалена 2026-05-23 — единственный путь открыть
    // мессенджер из произвольной страницы теперь идёт через топбарное меню.
    useEffect(() => {
        if (typeof window === 'undefined') return
        const onOpen = () => {
            setIsOpen(true)
            chat.loadRooms()
        }
        window.addEventListener('onboardix:messenger:open', onOpen)
        return () => window.removeEventListener('onboardix:messenger:open', onOpen)
    }, [chat])

    // Broadcast unread count для топбарного бейджа на AssistantsMenu.
    useEffect(() => {
        if (typeof window === 'undefined') return
        window.dispatchEvent(new CustomEvent('onboardix:messenger:unread', {
            detail: { count: chat.totalUnread || 0 },
        }))
    }, [chat.totalUnread])

    const mentionCandidates = (chat.activeRoom?.participants || [])
        .filter(p => String(p.user_id) !== String(user?.id))
        .map(p => ({ id: p.user_id, full_name: p.name || '', login: p.name || '' }))

    const canPin = user?.role === 'admin'
        || chat.activeRoom?.participants?.some(p =>
            String(p.user_id) === String(user?.id) && ['owner', 'admin'].includes(p.role),
        )

    // Handle send
    const handleSend = useCallback(async (payload) => {
        await chat.sendMessage(payload.content, {
            fileUrl: payload.fileUrl,
            fileName: payload.fileName,
            fileSize: payload.fileSize,
            mentionedUserIds: payload.mentionedUserIds,
            replyToId: payload.replyToId,
        })
        setReplyTo(null)
    }, [chat])

    // Handle typing
    const handleTyping = useCallback((isTyping) => {
        if (chat.activeRoom) {
            chat.sendTyping(chat.activeRoom.id, isTyping)
        }
    }, [chat])

    // Handle reaction
    const handleReaction = useCallback(async (messageId, emoji) => {
        await chatApi.toggleReaction(messageId, emoji)
    }, [])

    const handleDeleteMessage = useCallback(async (messageId) => {
        await chat.deleteMessage(messageId)
    }, [chat])

    const hasActive = !!chat.activeRoom

    const jumpToMessageInThread = useCallback(async (messageId) => {
        if (!messageId) return
        const present = document.getElementById(`chat-msg-${messageId}`)
        if (!present) {
            await chat.ensureMessageLoaded?.(messageId)
        }
        const node = document.getElementById(`chat-msg-${messageId}`)
        if (!node) return
        node.scrollIntoView({ behavior: 'smooth', block: 'center' })
        node.classList.add('chat-msg--flash')
        window.setTimeout(() => node.classList.remove('chat-msg--flash'), 1000)
    }, [chat])

    if (location.pathname.includes('messenger')) {
        return null
    }

    return (
        <>
            {/* FAB `.chat-toggle` удалён — открывается из топбарного меню «Помощники». */}
            {/* Overlay */}
            {isOpen && isExpanded && (
                <div className="chat-overlay" onClick={() => setIsExpanded(false)} />
            )}
            {/* Panel */}
            <div
                className={[
                    'chat-panel',
                    isOpen && 'chat-panel--open',
                    isExpanded && 'chat-panel--expanded',
                    hasActive && 'chat-panel--has-active',
                ].filter(Boolean).join(' ')}
                id="chat-panel"
            >
                {/* Panel Header */}
                <div className="chat-panel__header">
                    <div className="chat-panel__header-title">
                        <MessageCircle size={18} />
                        Мессенджер
                        {!chat.isConnected && (
                            <span
                                style={{
                                    fontWeight: 500
                                }}
                                className='text-xxs color-danger'>
                                • Офлайн
                            </span>
                        )}
                    </div>
                    <div className="chat-panel__header-actions">
                        <button
                            className="chat-panel__header-btn"
                            onClick={() => setIsExpanded(!isExpanded)}
                            title={isExpanded ? 'Свернуть' : 'Развернуть'}
                        >
                            {isExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                        </button>
                        <button
                            className="chat-panel__header-btn"
                            onClick={() => { setIsOpen(false); setIsExpanded(false) }}
                            title="Закрыть"
                        >
                            <X size={16} />
                        </button>
                    </div>
                </div>

                {/* Panel Body */}
                <div className="chat-panel__body">
                    {/* Room List */}
                    <RoomList
                        rooms={chat.rooms}
                        activeRoom={chat.activeRoom}
                        onSelect={chat.selectRoom}
                        onNewChat={() => { setNewChatTab('direct'); setShowNewChat(true) }}
                        onDeleteRoom={chat.leaveRoom}
                    />

                    {/* Chat Area */}
                    {hasActive && (
                        <div className="chat-area">
                            <ChatHeader
                                room={chat.activeRoom}
                                currentUserId={user?.id}
                                onBack={() => chat.setActiveRoom(null)}
                            />
                            <div className="chat-room-layout">
                                <div className="chat-room-layout__thread">
                                    <div
                                        style={{
                                            padding: '6px 10px 0'
                                        }}
                                        className='flex justify-end'>
                                        <button type="button" className="btn btn--secondary btn--sm" onClick={() => setShowRoomInfo((s) => !s)}>
                                            <Info size={14} /> {showRoomInfo ? 'Скрыть инфо' : 'Инфо о комнате'}
                                        </button>
                                    </div>
                                    <MessageArea
                                        messages={chat.messages}
                                        currentUserId={user?.id}
                                        typingUsers={chat.typingUsers}
                                        roomId={chat.activeRoom?.id}
                                        onReaction={handleReaction}
                                        roomParticipants={chat.activeRoom?.participants || []}
                                        roomType={chat.activeRoom?.type}
                                        onReply={(m) => setReplyTo(m)}
                                        onDelete={handleDeleteMessage}
                                        onLoadMore={chat.loadMoreMessages}
                                        hasMoreHistory={chat.hasMoreHistory}
                                        isLoadingMore={chat.isLoadingMore}
                                        unreadAnchorAt={chat.activeRoom?.current_user_last_read_at}
                                        onEnsureMessageLoaded={chat.ensureMessageLoaded}
                                        pinnedMessage={chat.activeRoom?.pinned_message}
                                        canPin={canPin}
                                        canModerate={canPin}
                                        onPinMessage={(id) => chat.pinRoomMessage(id)}
                                        onUnpin={() => chat.pinRoomMessage(null)}
                                    />
                                    <MessageInput
                                        onSend={handleSend}
                                        onTyping={handleTyping}
                                        disabled={false}
                                        liveSyncConnected={chat.isConnected}
                                        mentionCandidates={mentionCandidates}
                                        replyTo={replyTo}
                                        onCancelReply={() => setReplyTo(null)}
                                        roomId={chat.activeRoom?.id}
                                    />
                                </div>
                                {showRoomInfo && (
                                    <RoomInfoPanel
                                        room={chat.activeRoom}
                                        messages={chat.messages}
                                        onJumpToMessage={jumpToMessageInThread}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {showNewChat && (
                <NewChatDialog
                    chat={chat}
                    canCreateChannel={canCreateChannel}
                    initialTab={newChatTab}
                    onClose={() => setShowNewChat(false)}
                    onRoomCreated={(room) => {
                        setShowNewChat(false)
                        if (room?.id) void chat.selectRoom(room)
                    }}
                />
            )}
        </>
    );
}
